import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Option } from '@beeline/design-system-react/extracted-styles';

import { CreateTownParams } from 'src/api/plan';
import {
  defaultTimezone,
  timezones,
} from 'src/components/TimezoneSelect/TimezoneSelect';
import { getAdminState } from 'src/store/admin/selectors';
import { getTownsState } from 'src/store/towns/selectors';
import { changeTown, addTown } from 'src/store/towns/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface AdministratorTownFormValues {
  id: number | undefined;
  name: string;
  enabled: boolean;
  disabledReason: string;
  isTestData: boolean;
  timeZone: Option<string> | undefined;
}

const defaultFormValues: AdministratorTownFormValues = {
  id: undefined,
  name: '',
  enabled: true,
  disabledReason: '',
  isTestData: false,
  timeZone: timezones.get(defaultTimezone),
};

export interface AdministratorTownFormProps {
  children?: ReactNode;
}

export const AdministratorTownForm: FC<AdministratorTownFormProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const { selectedTownId } = useSelector(getAdminState);
  const { towns } = useSelector(getTownsState);

  const save = useCallback(
    async (values: AdministratorTownFormValues) => {
      const params: CreateTownParams = {
        townName: values.name,
        ownEnabled: values.enabled,
        ownDisabledReason: values.disabledReason,
        testData: values.isTestData,
        timeZone: values.timeZone?.value ? +values.timeZone.id : undefined,
      };

      if (values.id) {
        await dispatch(changeTown({ townId: values.id, ...params }));
      } else {
        await dispatch(addTown(params));
      }

      return true;
    },
    [dispatch],
  );

  const initialValues = useMemo((): AdministratorTownFormValues => {
    if (!selectedTownId) return defaultFormValues;

    const town = towns.get(selectedTownId);

    if (town)
      return {
        id: town.townId,
        name: town.townName,
        enabled: town.ownEnabled ?? false,
        disabledReason: town.ownDisabledReason ?? '',
        isTestData: town.testData ?? false,
        timeZone: town.timeZone ? timezones.get(town.timeZone) : undefined,
      };

    return defaultFormValues;
  }, [selectedTownId, towns]);

  return (
    <Form onSubmit={save} initialValues={initialValues}>
      {() => children}
    </Form>
  );
};
